/**
 * 场景对象（P0-3c）：store 里一个 DirectorObject ↔ 视口里一棵可交互子树。
 *
 * 结构：外层 group 承载 transform（gizmo 附着点 / 拾取边界），
 *       内层 group 承载 uniformScale——两者分离是因为 gizmo 缩放拖的是
 *       group.scale（对应 transform.scale），混用 uniformScale 会让面板和拖拽互相打架。
 *
 * 关键约定：
 *  · transform 用 effect 命令式写入 group（gizmo 拖拽→onObjectChange→store→本 effect
 *    回写同值，闭环幂等）；
 *  · **所有对象（含几何体）都登记进 runtimeRegistry 并在 group 上标记 userData.ownerId**——
 *    第七轮实测：R3F 声明式 onClick 对角色仍选不中（疑与 drei TransformControls 的
 *    指针事件拦截相关），拾取改为视口层统一手动 raycast（ClickPicker），
 *    本组件不再自带 onClick；
 *  · 角色异步加载完成后补登记 root/rest；卸载必须注销。
 */
import { useEffect, useRef, useState } from 'react';
import type { Group } from 'three';

import type { DirectorObject, GeometryType } from '../../model/director';
import { BODY_TYPE_MODEL, isCharacterBody } from './assets';
import { acquireCharacter, type PreparedCharacter } from './modelCache';
import { applyRigToModel, rigHasPose } from './rigRender';
import { getRuntime, registerRuntime, unregisterRuntime } from './runtimeRegistry';

const DEG_TO_RAD = Math.PI / 180;

/** 6 种几何体：站位参考用，尺寸按"人可站立/可对话"的量级给 */
function GeometryBody({ type, tint }: { type: GeometryType; tint: string }) {
  switch (type) {
    case 'box':
      return (
        <mesh position={[0, 0.5, 0]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial color={tint} roughness={0.85} />
        </mesh>
      );
    case 'sphere':
      return (
        <mesh position={[0, 0.55, 0]}>
          <sphereGeometry args={[0.55, 24, 16]} />
          <meshStandardMaterial color={tint} roughness={0.85} />
        </mesh>
      );
    case 'cylinder':
      return (
        <mesh position={[0, 0.9, 0]}>
          <cylinderGeometry args={[0.3, 0.3, 1.8, 20]} />
          <meshStandardMaterial color={tint} roughness={0.85} />
        </mesh>
      );
    case 'torus':
      return (
        <mesh position={[0, 0.55, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.45, 0.16, 16, 32]} />
          <meshStandardMaterial color={tint} roughness={0.85} />
        </mesh>
      );
    case 'cone':
      return (
        <mesh position={[0, 0.75, 0]}>
          <coneGeometry args={[0.5, 1.5, 24]} />
          <meshStandardMaterial color={tint} roughness={0.85} />
        </mesh>
      );
    case 'pyramid':
      return (
        <mesh position={[0, 0.75, 0]}>
          {/* 棱锥 = 4 段圆锥，省一个自定义几何体实现 */}
          <coneGeometry args={[0.62, 1.5, 4]} />
          <meshStandardMaterial color={tint} roughness={0.85} />
        </mesh>
      );
  }
}

/** 选中线框盒已搬到 DirectorViewport 的 SelectionBoxes（世界空间根层）——
 *  第六轮实测 bug：旧版把世界坐标包围盒挂在对象 group 下，变换被乘两次，
 *  选中框飞到远处。教训：世界空间的量测结果只能放在世界空间根层渲染。 */

export interface SceneObjectProps {
  obj: DirectorObject;
  onError: (message: string) => void;
}

export function SceneObject({ obj, onError }: SceneObjectProps) {
  const groupRef = useRef<Group>(null);
  const [prepared, setPrepared] = useState<PreparedCharacter | null>(null);
  const charBody = isCharacterBody(obj.bodyType) ? obj.bodyType : null;

  // 归属标记：手动 raycast 命中任意子孙后向上走找 ownerId，才能知道"点的是谁"
  useEffect(() => {
    const g = groupRef.current;
    if (g !== null) {
      g.userData.ownerId = obj.id;
    }
  }, [obj.id]);

  // 几何体对象没有异步加载，挂载即注册（root=null），让 ClickPicker/选中框统一取用
  useEffect(() => {
    if (charBody !== null) {
      return;
    }
    const g = groupRef.current;
    if (g === null) {
      return;
    }
    registerRuntime(obj.id, { group: g, root: null, rest: new Map() });
    return () => {
      unregisterRuntime(obj.id);
    };
  }, [obj.id, charBody]);

  useEffect(() => {
    if (charBody === null) {
      return;
    }
    let cancelled = false;
    void acquireCharacter(BODY_TYPE_MODEL[charBody])
      .then((p) => {
        if (cancelled) {
          return;
        }
        const g = groupRef.current;
        if (g !== null) {
          // 归属标记三层冗余：group/root/proxy 任一层被命中都能溯源（第九轮诊断教训）
          g.userData.ownerId = obj.id;
          p.root.userData.ownerId = obj.id;
          registerRuntime(obj.id, { group: g, root: p.root, rest: p.rest, bounds: p.bounds });
        }
        setPrepared(p);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          onError(`「${obj.name}」模型加载失败：${String(e)}`);
        }
      });
    return () => {
      cancelled = true;
      unregisterRuntime(obj.id);
    };
    // 故意不含 obj.name：P0-4 属性面板可改名，若入依赖每次改名都会重拉模型；
    // name 只用在错误文案里，旧值显示一次无伤大雅
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obj.id, charBody, onError]);

  // store.transform → group（命令式；gizmo 拖拽回写后本 effect 写入同值，幂等）。
  // 第九轮诊断教训：这个 effect 总在 R3F 真正挂好 group 后运行，所以这里
  // 用当前 ref 重新注册一次（保留已有 root/rest），保证注册表里永远是"活 group"
  // 而非 StrictMode/重建场景下的陈旧引用——诊断里 335px 偏移的即这类幽灵引用。
  useEffect(() => {
    const g = groupRef.current;
    if (g === null) {
      return;
    }
    const [px, py, pz] = obj.transform.position;
    const [rx, ry, rz] = obj.transform.rotation;
    const [sx, sy, sz] = obj.transform.scale;
    g.position.set(px, py, pz);
    g.rotation.set(rx * DEG_TO_RAD, ry * DEG_TO_RAD, rz * DEG_TO_RAD);
    g.scale.set(sx, sy, sz);
    g.userData.ownerId = obj.id;
    const prev = getRuntime(obj.id);
    registerRuntime(obj.id, {
      group: g,
      root: prev?.root ?? null,
      rest: prev?.rest ?? new Map(),
      bounds: prev?.bounds,
    });
  }, [obj.id, obj.transform]);

  // 模型就绪那一刻按存档 obj.rig 摆好姿势（P0-8 修复：刷新恢复/打开文件后姿势自动重现）。
  // 只依赖 prepared（模型加载完成）：后续滑杆/预设/逐骨的实时编辑由页面命令式 renderRig 驱动，
  // 不在此重复施加，避免与拖拽互打架。
  useEffect(() => {
    const rig = obj.rig;
    if (prepared === null || rig === undefined || !rigHasPose(rig)) {
      return;
    }
    void applyRigToModel(prepared.root, prepared.rest, rig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepared]);

  // 名称标签已移到 DirectorViewport 的 ObjectLabels（世界层、由 store objects 驱动，
  // 修“刷新后多角色只剩一个标签”：不再依赖每实例异步 prepared）。

  return (
    <group ref={groupRef} visible={obj.visible}>
      <group scale={[obj.uniformScale, obj.uniformScale, obj.uniformScale]}>
        {charBody !== null ? (
          prepared === null ? null : (
            <>
              <primitive object={prepared.root} />
              {/* 拾取代理（第六轮实测：角色点不中的修复）：SkinnedMesh 射线检测不可靠，
                  用模型包围盒生成一个不可见但可射的盒子接管点击。
                  colorWrite=false 保证它在任何材质下都画不出来，但参与 raycast */}
              <mesh position={prepared.bounds.center} userData={{ ownerId: obj.id }}>
                <boxGeometry args={prepared.bounds.size} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
              </mesh>
            </>
          )
        ) : (
          <GeometryBody type={obj.geometryType ?? 'box'} tint={obj.tint} />
        )}
      </group>
    </group>
  );
}
